import { useState } from "react";
import DoospLayout from "@/components/doosp/DoospLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { GraduationCap, Award, Users, CheckCircle2, Send, Sparkles } from "lucide-react";
import { PROGRAMS } from "@/lib/programs";

export default function BecomeInstructor() {
  const [form, setForm] = useState({
    name: "", email: "", credentials: "", specialty: "", years: "", experience: "",
    programs: [], portfolio: "",
  });
  const [sent, setSent] = useState(false);

  const toggleProgram = (slug) => {
    setForm((f) => ({
      ...f,
      programs: f.programs.includes(slug) ? f.programs.filter((s) => s !== slug) : [...f.programs, slug],
    }));
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.experience.trim()) {
      return toast.error("Name, email, and experience summary are required");
    }
    setSent(true);
  };

  if (sent) {
    return (
      <DoospLayout>
        <section className="min-h-[60vh] flex items-center justify-center">
          <div className="max-w-lg mx-auto px-6 py-24 text-center">
            <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="font-display text-3xl md:text-4xl font-bold" data-testid="instructor-sent">Application received.</h1>
            <p className="text-zinc-600 mt-4">
              Thank you, {form.name.split(" ")[0]}. Our program directors review every application personally and will reach out within 5 business days.
            </p>
          </div>
        </section>
      </DoospLayout>
    );
  }

  return (
    <DoospLayout>
      <section className="border-b border-zinc-200 bg-gradient-to-b from-white to-zinc-50">
        <div className="max-w-5xl mx-auto px-6 py-24">
          <div className="text-xs font-mono uppercase tracking-widest text-red-800 mb-4">Become an instructor</div>
          <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="instructor-heading">
            Teach the next generation of<br />
            <span className="text-red-800">safety professionals.</span>
          </h1>
          <p className="text-lg text-zinc-600 mt-6 max-w-3xl leading-relaxed">
            DOOSP instructors author curriculum, review learner submissions, and shape the
            standards. If you&rsquo;re a working practitioner with subject-matter depth in
            occupational safety, infection control, or a related field, we&rsquo;d like to hear from you.
          </p>
        </div>
      </section>

      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-8">
          {[
            { icon: GraduationCap, title: "Shape curricula", body: "Contribute to new modules, revise existing content when standards change, and set assessment rubrics." },
            { icon: Award, title: "Earn recognition", body: "Your name appears on every course you author. Instructors are recognized in our directory and marketing materials." },
            { icon: Users, title: "Reach practitioners globally", body: "Our learners span healthcare, first response, hospitality, and safety-critical industries in 40+ countries." },
          ].map((b) => (
            <div key={b.title} className="text-left">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-red-50 mb-4">
                <b.icon className="h-6 w-6 text-red-800" />
              </div>
              <div className="font-display font-bold text-xl">{b.title}</div>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-zinc-50">
        <div className="max-w-3xl mx-auto px-6 py-20">
          <h2 className="font-display text-3xl md:text-4xl font-bold">Application</h2>
          <p className="text-zinc-600 mt-2">Takes about 5 minutes. All fields marked * are required.</p>

          <form onSubmit={submit} className="mt-8 bg-white border border-zinc-200 rounded-xl p-8 space-y-5" data-testid="instructor-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Full name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="instructor-name" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="instructor-email" />
              </div>
              <div>
                <Label>Credentials (RN, MD, MPH, CIH, etc.)</Label>
                <Input value={form.credentials} onChange={(e) => setForm({ ...form, credentials: e.target.value })} data-testid="instructor-credentials" />
              </div>
              <div>
                <Label>Primary specialty</Label>
                <Input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} placeholder="e.g. Infection prevention" data-testid="instructor-specialty" />
              </div>
              <div>
                <Label>Years in practice</Label>
                <Input value={form.years} onChange={(e) => setForm({ ...form, years: e.target.value })} type="number" min={0} data-testid="instructor-years" />
              </div>
              <div>
                <Label>Portfolio / LinkedIn</Label>
                <Input value={form.portfolio} onChange={(e) => setForm({ ...form, portfolio: e.target.value })} placeholder="https://…" data-testid="instructor-portfolio" />
              </div>
            </div>

            <div>
              <Label>Programs you&rsquo;d like to teach</Label>
              <div className="grid sm:grid-cols-2 gap-2 mt-2">
                {PROGRAMS.map((p) => (
                  <label key={p.slug} className="flex items-start gap-3 p-3 rounded-md border border-zinc-200 hover:border-red-600 cursor-pointer bg-white">
                    <input
                      type="checkbox"
                      checked={form.programs.includes(p.slug)}
                      onChange={() => toggleProgram(p.slug)}
                      className="mt-0.5"
                      data-testid={`instructor-program-${p.slug}`}
                    />
                    <div>
                      <div className="text-sm font-medium">{p.shortName}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">{p.tagline}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label>Brief experience summary *</Label>
              <textarea
                value={form.experience}
                onChange={(e) => setForm({ ...form, experience: e.target.value })}
                rows={5}
                required
                className="w-full border border-zinc-200 rounded-md p-3 text-sm resize-none"
                placeholder="Tell us about your relevant clinical or safety-officer experience, published work, or teaching background."
                data-testid="instructor-experience"
              />
            </div>

            <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white h-11 px-6" data-testid="instructor-submit">
              <Send className="mr-2 h-4 w-4" /> Submit application
            </Button>
          </form>
        </div>
      </section>

      <section className="bg-gradient-to-r from-red-800 to-red-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-14 text-center">
          <Sparkles className="h-6 w-6 text-red-200 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-lg font-display">
            &ldquo;Being a DOOSP instructor gave me a platform to translate 15 years of biosafety work into
            something learners actually use in the field.&rdquo;
          </p>
          <p className="text-sm text-red-200 mt-3 font-mono uppercase tracking-widest">
            Dr. Priya Rao · DOOSP faculty
          </p>
        </div>
      </section>
    </DoospLayout>
  );
}
